import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';

import Button from '../../../components/ui/Button';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import PanelCard from '../../performance-operations/components/PanelCard';
import { Badge, Sub } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { thresholdConditionText } from '../../../components/performance/goalTypes';
import {
  PAGE_SIZE, completionText, dayOf, downloadBlob, exportGoals, fetchGoals, formatNumber, ownerProfileLink,
} from '../config';

// E04 alt alan — hedef tablosu (analiz: çalışan, hedef, ölçü birimi, yön, baz, hedef değer, güncel değer, gerçekleşme,
// ağırlık, kanıt ve son tarih). Kişi kümesi (yönetici: doğrudan ekip, SkipLevel açıksa alt ağaç; İK: kiracı). Sunucu
// tarafı sayfalama ve sıralama; satır → hedef detayı (güncelleme geçmişi çekmecede). Excel indirmesi aynı süzgeçle.
// Tek kaydırma: tablo kendi içinde kaydırmaz (tableBodyMaxHeight="none") — sayfa gövdesi kayar.
const SORT_BY_COLUMN = { title: 'title', dueAt: 'dueAt', lastUpdateAt: 'lastUpdate', rawPct: 'progress' };

export default function GoalListPanel({
  filters, goalFilters, onOpenGoal, refreshKey, personScope,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;
  const [paging, setPaging] = useState({ pageNumber: 1, pageSize: PAGE_SIZE });
  const [sort, setSort] = useState({ sort: undefined, order: undefined });
  const [downloading, setDownloading] = useState(false);
  const key = JSON.stringify([filters, goalFilters]);

  // Süzgeç değişince ilk sayfaya dönülür.
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) {
    setLastKey(key);
    setPaging((p) => ({ ...p, pageNumber: 1 }));
  }

  const panel = usePanel(
    () => fetchGoals(filters, goalFilters, { ...paging, ...sort }),
    [key, paging.pageNumber, paging.pageSize, sort.sort, sort.order, refreshKey],
    !!filters.periodId,
  );
  const rows = panel.data?.rows || [];

  const download = useCallback(async () => {
    setDownloading(true);
    const res = await exportGoals(filters, goalFilters, sort);
    downloadBlob(res, 'hedef-analitigi.xlsx');
    setDownloading(false);
  }, [filters, goalFilters, sort]);

  const columns = useMemo(() => [
    { name: 'goalId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.goalAnalytics.col.employee'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          const href = ownerProfileLink(row?.employeeId, filters.periodId, from);
          return href ? <Link to={href}>{v || '-'}</Link> : (v || '-');
        },
      },
    },
    {
      name: 'title',
      label: t('performance.goalAnalytics.col.goal'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          return (
            <>
              <div>{v}</div>
              <Sub>
                {t(`performance.goal.goalType.${row?.goalType}`, { defaultValue: row?.goalType })}
                {row?.goalType === 'threshold' ? ` · ${thresholdConditionText(t, { kpiIndicatorName: '', kpiUnit: row.unit, thresholdOperator: row.thresholdOperator, thresholdValue: row.targetValue })}` : ''}
                {row?.goalType === 'milestone' && row.stepsTotal != null ? ` · ${t('performance.goal.milestone.acceptedOf', { accepted: row.stepsAccepted, total: row.stepsTotal })}` : ''}
              </Sub>
            </>
          );
        },
      },
    },
    {
      name: 'unit',
      label: t('performance.goalAnalytics.col.unit'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? t(`performance.goal.kpiUnit.${v}`, { defaultValue: v }) : '-'),
      },
    },
    {
      name: 'direction',
      label: t('performance.goalAnalytics.col.direction'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? t(`performance.goal.kpiDirection.${v}`) : '-'),
      },
    },
    {
      name: 'baselineValue',
      label: t('performance.goalAnalytics.col.baseline'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v != null ? formatNumber(v, lang) : '-') },
    },
    {
      name: 'targetValue',
      label: t('performance.goalAnalytics.col.target'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v != null ? formatNumber(v, lang) : '-') },
    },
    {
      name: 'currentValue',
      label: t('performance.goalAnalytics.col.current'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          if (row?.goalType === 'threshold' && row.thresholdOperator === 'binary') {
            return row.thresholdMet == null ? '-' : t(row.thresholdMet ? 'performance.goal.threshold.yes' : 'performance.goal.threshold.no');
          }
          return v != null ? formatNumber(v, lang) : '-';
        },
      },
    },
    {
      name: 'rawPct',
      label: t('performance.goalAnalytics.col.completion'),
      options: {
        filter: false, sort: true,
        customBodyRender: (_, meta) => completionText(rows[meta.rowIndex], lang) || t('performance.goalAnalytics.kpi.noData'),
      },
    },
    {
      name: 'weightPct',
      label: t('performance.goalAnalytics.col.weight'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v != null ? `${formatNumber(v, lang)}%` : '-') },
    },
    {
      name: 'lastUpdateAt',
      label: t('performance.goalAnalytics.col.evidence'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          return (
            <>
              <div>{dayOf(v)}</div>
              <Badge $tone={row?.evidenceFresh ? 'success' : 'danger'}>
                {row?.evidenceFresh
                  ? t('performance.goalAnalytics.evidence.fresh')
                  : t('performance.goalAnalytics.evidence.staleDays', { days: row?.daysSinceUpdate })}
              </Badge>
            </>
          );
        },
      },
    },
    {
      name: 'dueAt',
      label: t('performance.goalAnalytics.col.due'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          return (
            <>
              <div>{dayOf(v)}</div>
              {row?.overdue ? <Badge $tone="danger">{t('performance.goalAnalytics.overdue')}</Badge> : null}
            </>
          );
        },
      },
    },
    {
      name: 'riskReasons',
      label: t('performance.goalAnalytics.col.risk'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => ((v || []).length === 0 ? '-' : (
          <>
            {(v || []).map((r) => <Badge key={r} $tone="danger">{t(`performance.goalAnalytics.risk.${r}`)}</Badge>)}
          </>
        )),
      },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, meta) => {
          const row = rows[meta.rowIndex];
          if (!row) return null;
          return (
            <OptionsMenu
              row={row}
              actions={[{ type: 'details', label: t('performance.goalAnalytics.action.detail'), onClick: () => onOpenGoal?.(row) }]}
            />
          );
        },
      },
    },
  ], [t, lang, rows, filters.periodId, from, onOpenGoal]);

  const onTableChanged = useCallback((action, state) => {
    if (action === 'changePage') setPaging((p) => ({ ...p, pageNumber: state.page + 1 }));
    else if (action === 'changeRowsPerPage') setPaging({ pageNumber: 1, pageSize: state.rowsPerPage });
    else if (action === 'sort') {
      const by = SORT_BY_COLUMN[state.sortOrder?.name];
      setSort(by ? { sort: by, order: state.sortOrder.direction === 'desc' ? 'desc' : 'asc' } : { sort: undefined, order: undefined });
      setPaging((p) => ({ ...p, pageNumber: 1 }));
    }
  }, []);

  return (
    <PanelCard
      panelKey="goalList"
      title={t('performance.goalAnalytics.panel.list')}
      span={12}
      error={panel.error}
      onRetry={panel.retry}
    >
      {personScope === 'direct' ? <Sub data-role="scope-note">{t('performance.goalAnalytics.panel.directScopeNote')}</Sub> : null}
      <DataTable
        title={t('performance.goalAnalytics.panel.listTable', { count: panel.data?.total ?? 0 })}
        fetching={panel.loading}
        columns={columns}
        data={rows}
        totalRowCount={panel.data?.total || 0}
        pageIndex={panel.data?.pageIndex || 0}
        pageSize={paging.pageSize}
        onTableChanged={onTableChanged}
        onRowClick={(row) => onOpenGoal?.(row)}
        customIdSelector="goalId"
        tableBodyMaxHeight="none"
        disableFilters
        toolbarActions={(
          <Button variant="outline" onClick={download} loading={downloading} data-action="export-goals">
            <Download size={14} />
            {' '}
            {t('performance.goalAnalytics.action.export')}
          </Button>
        )}
      />
    </PanelCard>
  );
}
